import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {AiSegmentSuggestionService} from "../../service/api/ai-segment-suggestion.service";
import {AiSuggestionFeedbackService} from "../../service/api/ai-suggestion-feedback.service";
import {
    ApplySuggestionRequest,
    GenerateSegmentSuggestionsRequest,
    SubmitFeedbackRequest,
    SuggestionDto
} from "../../model/dto/api/ai-segment-suggestion.interface";
import {AiSegmentSuggestionDto} from "../../model/dto/entity/ai-segment-suggestion.dto";

/**
 * AI Segment Suggestions Component.
 * F-024: AI-Powered Segment Suggestions - Displays AI-generated segment recommendations
 * and allows users to apply, reject, or provide feedback on suggestions.
 */
@Component({
    selector: 'ct-ai-segment-suggestions',
    templateUrl: './ai-segment-suggestions.component.html',
    standalone: false
})
export class AiSegmentSuggestionsComponent implements OnInit {

    @Input() campaignId: number;
    @Input() targetType: string = 'CUSTOMER';

    @Output() suggestionApplied = new EventEmitter<{ suggestionId: number; campaignId: number }>();
    @Output() suggestionRejected = new EventEmitter<number>();

    suggestions: SuggestionDto[] = [];
    existingSuggestions: AiSegmentSuggestionDto[] = [];
    loading: boolean = false;
    generating: boolean = false;
    errorMessage: string = '';

    campaignGoal: string = '';
    showGenerateDialog: boolean = false;
    showFeedbackDialog: boolean = false;
    selectedSuggestion: SuggestionDto = null;
    feedbackRating: number = 0;
    feedbackComments: string = '';

    constructor(
        private suggestionService: AiSegmentSuggestionService,
        private feedbackService: AiSuggestionFeedbackService
    ) {
    }

    ngOnInit(): void {
        this.loadExistingSuggestions();
    }

    loadExistingSuggestions(): void {
        if (!this.campaignId) return;

        this.loading = true;
        this.suggestionService.getSuggestionsForCampaign(this.campaignId).subscribe({
            next: (suggestions) => {
                this.existingSuggestions = suggestions;
                this.loading = false;
            },
            error: (error) => {
                console.error('Failed to load suggestions', error);
                this.loading = false;
            }
        });
    }

    openGenerateDialog(): void {
        this.showGenerateDialog = true;
        this.campaignGoal = '';
    }

    generateSuggestions(): void {
        if (!this.campaignGoal.trim()) {
            this.errorMessage = 'Bitte geben Sie ein Kampagnenziel ein';
            return;
        }

        this.generating = true;
        this.errorMessage = '';

        const request: GenerateSegmentSuggestionsRequest = {
            campaignId: this.campaignId,
            campaignGoal: this.campaignGoal,
            targetType: this.targetType,
            maxSuggestions: 5,
            minConfidence: 0.5
        };

        this.suggestionService.generateSuggestions(request).subscribe({
            next: (response) => {
                if (response.success) {
                    this.suggestions = response.suggestions;
                    this.showGenerateDialog = false;
                } else {
                    this.errorMessage = response.message;
                }
                this.generating = false;
            },
            error: (error) => {
                this.errorMessage = 'Fehler beim Generieren der Vorschläge. Bitte versuchen Sie es erneut.';
                this.generating = false;
            }
        });
    }

    applySuggestion(suggestion: SuggestionDto): void {
        const request: ApplySuggestionRequest = {
            suggestionId: suggestion.suggestionId,
            campaignId: this.campaignId,
            replaceExisting: false,
            previewFirst: true
        };

        this.suggestionService.applySuggestion(request).subscribe({
            next: (response) => {
                if (response.success) {
                    this.suggestionApplied.emit({
                        suggestionId: suggestion.suggestionId,
                        campaignId: this.campaignId
                    });
                    this.loadExistingSuggestions();
                } else {
                    this.errorMessage = response.message;
                }
            },
            error: (error) => {
                this.errorMessage = 'Fehler beim Anwenden des Vorschlags. Bitte versuchen Sie es erneut.';
            }
        });
    }

    rejectSuggestion(suggestion: SuggestionDto): void {
        this.suggestionService.rejectSuggestion(suggestion.suggestionId).subscribe({
            next: () => {
                this.suggestionRejected.emit(suggestion.suggestionId);
                this.suggestions = this.suggestions.filter(s => s.suggestionId !== suggestion.suggestionId);
            },
            error: (error) => {
                this.errorMessage = 'Fehler beim Ablehnen des Vorschlags. Bitte versuchen Sie es erneut.';
            }
        });
    }

    openFeedbackDialog(suggestion: SuggestionDto): void {
        this.selectedSuggestion = suggestion;
        this.feedbackRating = 0;
        this.feedbackComments = '';
        this.showFeedbackDialog = true;
    }

    submitFeedback(): void {
        if (!this.selectedSuggestion) return;

        const request: SubmitFeedbackRequest = {
            suggestionId: this.selectedSuggestion.suggestionId,
            rating: this.feedbackRating,
            comments: this.feedbackComments,
            wasHelpful: this.feedbackRating >= 4
        };

        this.feedbackService.submitFeedback(request).subscribe({
            next: (response) => {
                if (response.success) {
                    this.showFeedbackDialog = false;
                    this.selectedSuggestion = null;
                }
            },
            error: (error) => {
                this.errorMessage = 'Fehler beim Senden des Feedbacks. Bitte versuchen Sie es erneut.';
            }
        });
    }

    getConfidenceColor(score: number): string {
        if (score >= 0.8) return 'text-green-600';
        if (score >= 0.6) return 'text-yellow-600';
        return 'text-red-600';
    }

    getConfidenceLabel(score: number): string {
        if (score >= 0.8) return 'Hoch';
        if (score >= 0.6) return 'Mittel';
        return 'Niedrig';
    }

    getStatusColor(status: string): string {
        switch (status?.toLowerCase()) {
            case 'pending':
                return 'bg-gray-200 text-gray-800';
            case 'accepted':
                return 'bg-green-200 text-green-800';
            case 'rejected':
                return 'bg-red-200 text-red-800';
            case 'modified':
                return 'bg-yellow-200 text-yellow-800';
            default:
                return 'bg-gray-200 text-gray-800';
        }
    }
}
